/**
 * Paramedical Service
 * Handles all paramedical service-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';

class ParamedicalService {
  /**
   * Get list of paramedical services
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search query (optional)
   * @param {string} params.type - Filter by service type (optional)
   * @returns {Promise<Object>} Response with services array
   */
  async getParamedicalServices(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.type) queryParams.append('type', params.type);

      const response = await axios.get(
        `${API_ENDPOINTS.PARAMEDICAL.LIST}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get paramedical service detail
   * @param {number} serviceId - Service ID
   * @returns {Promise<Object>} Response with service details
   */
  async getServiceDetail(serviceId) {
    try {
      const response = await axios.get(API_ENDPOINTS.PARAMEDICAL.DETAIL(serviceId));
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user's paramedical service appointments
   * @returns {Promise<Object>} Response with appointments array
   */
  async getUserAppointments() {
    try {
      const response = await axios.get(API_ENDPOINTS.PARAMEDICAL.APPOINTMENTS);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Book paramedical service
   * @param {Object} bookingData - Booking data
   * @param {number} bookingData.service_id - Service ID (required)
   * @param {string} bookingData.appointment_date - Date YYYY-MM-DD (required)
   * @param {string} bookingData.appointment_time - Time HH:MM 24-hour format (required)
   * @param {string} bookingData.address - Full address (required)
   * @param {number} bookingData.latitude - Location latitude (required)
   * @param {number} bookingData.longitude - Location longitude (required)
   * @returns {Promise<Object>} Response with booking details and pricing
   */
  async bookParamedicalService(bookingData) {
    try {
      const response = await axios.post(API_ENDPOINTS.PARAMEDICAL.BOOK, {
        service_id: bookingData.service_id,
        appointment_date: bookingData.appointment_date,
        appointment_time: bookingData.appointment_time,
        address: bookingData.address,
        latitude: bookingData.latitude,
        longitude: bookingData.longitude,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reschedule paramedical appointment
   * @param {number} appointmentId - Appointment ID
   * @param {Object} data - Reschedule data
   * @param {string} data.new_date - New date YYYY-MM-DD
   * @param {string} data.new_time - New time HH:MM
   * @returns {Promise<Object>} Response with updated appointment
   */
  async rescheduleAppointment(appointmentId, data) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.PARAMEDICAL.RESCHEDULE(appointmentId),
        {
          new_date: data.new_date,
          new_time: data.new_time,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Cancel paramedical appointment
   * @param {number} appointmentId - Appointment ID
   * @param {string} reason - Cancellation reason
   * @returns {Promise<Object>} Response with cancellation confirmation
   */
  async cancelAppointment(appointmentId, reason) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.PARAMEDICAL.CANCEL(appointmentId),
        { reason: reason }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get paramedical appointment history
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} Response with history and pagination
   */
  async getHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 20,
      });

      const response = await axios.get(
        `${API_ENDPOINTS.PARAMEDICAL.HISTORY}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generate invoice for paramedical appointment
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<Object>} Response with invoice details
   */
  async generateInvoice(appointmentId) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.PARAMEDICAL.GENERATE_INVOICE(appointmentId)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Download invoice for paramedical appointment
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<Blob>} PDF file blob
   */
  async downloadInvoice(appointmentId) {
    try {
      const response = await axios.get(
        API_ENDPOINTS.PARAMEDICAL.DOWNLOAD_INVOICE(appointmentId),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Calculate travel cost
   * @param {number} distanceKm - Distance in kilometers
   * @returns {number} Travel cost amount
   */
  calculateTravelCost(distanceKm) {
    return distanceKm * 3; // ₹3 per km
  }

  /**
   * Check if location is serviceable
   * @param {number} distanceKm - Distance in kilometers
   * @returns {boolean} Whether location is within service radius
   */
  isServiceable(distanceKm) {
    return distanceKm <= 30; // Maximum 30 km radius
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const errorMessage = error.response.data.error || error.response.data.message || 'Something went wrong';
      return new Error(errorMessage);
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return new Error('An error occurred. Please try again.');
    }
  }
}

export default new ParamedicalService();
